import styled, { useTheme } from 'styled-components'
import { useMemo } from 'react'
import { resolveWindow, windowOverlaps, type LoadWindow } from '../loadRules'

const ChartContainer = styled.div`
  overflow-x: auto;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const ChartWrapper = styled.div`
  min-width: 100%;
  display: flex;
`

const LabelsColumn = styled.div`
  min-width: 200px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
  position: sticky;
  left: 0;
  z-index: 10;
`

const LabelHeader = styled.div`
  height: 50px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  font-weight: 600;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
`

interface LabelRowProps {
    borderColor: string
}

const LabelRow = styled.div<LabelRowProps>`
  height: 40px;
  padding: 0 0.75rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ borderColor }) => borderColor};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  background: #fafafa;

  &:nth-child(even) {
    background: white;
  }

  &:hover {
    background: #e8eff2;
  }
`

const ChartArea = styled.div`
  flex: 1;
  min-width: 600px;
  overflow: hidden;
`

const ChartHeader = styled.div`
  height: 50px;
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
`

interface MonthCellProps {
    colWidth: number
}

const MonthCell = styled.div<MonthCellProps>`
  flex: 1;
  min-width: ${({ colWidth }) => colWidth}px;
  text-align: center;
  padding: 0.5rem 0.25rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  border-right: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-right: none;
  }
`

const RowsContainer = styled.div`
  position: relative;
`

interface TimelineRowProps {
    colWidth: number
}

const TimelineRow = styled.div<TimelineRowProps>`
  height: 40px;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  background: #fafafa;

  &:nth-child(even) {
    background: white;
  }

  & > div {
    flex: 1;
    min-width: ${({ colWidth }) => colWidth}px;
    border-right: 1px solid #f0f0f0;
  }

  & > div:last-child {
    border-right: none;
  }
`

const TodayLine = styled.div<{ left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ left }) => left}%;
  width: 2px;
  background: #F44336;
  z-index: 5;
`

const TodayLabel = styled.div`
  position: absolute;
  top: -20px;
  transform: translateX(-50%);
  background: #F44336;
  color: white;
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 4px;
`

interface BarProps {
    left: number
    width: number
    color: string
    top: number
    height?: number
    opacity?: number
}

const Bar = styled.div<BarProps>`
  position: absolute;
  height: ${({ height = 14 }) => height}px;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}%;
  width: ${({ width }) => width}%;
  background: ${({ color }) => color};
  opacity: ${({ opacity = 1 }) => opacity};
  border-radius: 4px;
  cursor: default;
`

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`

interface TeamChartProps {
    data: any
    dateFrom: string
    dateTo: string
    granularity: string
    onPersonClick: (member: any) => void
}

// Vertical layout of a 40px row. Opportunity bars sit centred; when the person
// also has LinkedIn tasks the row splits into two lanes so a task bar never
// covers the opportunity bar it overlaps in time.
const OPPORTUNITY_TOP = 13
const OPPORTUNITY_TOP_WITH_TASKS = 4
const TASK_TOP = 22
const TASK_HEIGHT = 12

/** Effective [start, end] of an opportunity row, open-ended rules included (null = no dates). */
const projectWindow = (project: any): LoadWindow | null => resolveWindow(
    project.effective_start_date || project.oferta_fecha_inicio || project.proyecto_fecha_inicio,
    project.effective_end_date || project.proyecto_fecha_fin || project.oferta_fecha_entrega,
)

// Same bands as the legend ('≤50%' green) and the bar colors below: exactly
// 50% is green, not orange.
export const getLoadColor = (capacity: number): string => {
    if (capacity === 0) return '#9E9E9E'
    if (capacity <= 50) return '#4CAF50'
    if (capacity <= 80) return '#FF9800'
    return '#F44336'
}

export const TeamChart: React.FC<TeamChartProps> = ({
    data,
    dateFrom,
    dateTo,
    granularity,
    onPersonClick,
}) => {
    const theme = useTheme()
    // Solid fixed color for LinkedIn learning-task bars (separate lane).
    const linkedinColor = theme.colors.linkedinAssignment

    const colWidth = useMemo(() => {
        switch (granularity) {
            case 'days': return 40
            case 'weeks': return 80
            default: return 100
        }
    }, [granularity])

    const periods = useMemo(() => {
        const result: { start: Date; label: string }[] = []
        const from = new Date(dateFrom)
        const to = new Date(dateTo)
        const current = new Date(from)

        switch (granularity) {
            case 'days':
                while (current <= to) {
                    result.push({
                        start: new Date(current),
                        label: current.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
                    })
                    current.setDate(current.getDate() + 1)
                }
                break
            case 'weeks':
                const startOfWeek = new Date(current)
                startOfWeek.setDate(current.getDate() - current.getDay() + 1)
                while (startOfWeek <= to) {
                    result.push({
                        start: new Date(startOfWeek),
                        label: startOfWeek.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
                    })
                    startOfWeek.setDate(startOfWeek.getDate() + 7)
                }
                break
            default:
                current.setDate(1)
                while (current <= to) {
                    result.push({
                        start: new Date(current),
                        label: current.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
                    })
                    current.setMonth(current.getMonth() + 1)
                }
        }

        return result
    }, [dateFrom, dateTo, granularity])

    const todayPosition = useMemo(() => {
        const today = new Date()
        const from = new Date(dateFrom)
        const to = new Date(dateTo)

        if (today < from || today > to) return null

        const totalDays = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
        const dayOffset = (today.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
        return (dayOffset / totalDays) * 100
    }, [dateFrom, dateTo])

    const calculateBarPosition = (start: Date | null, end: Date | null) => {
        if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return null

        const from = new Date(dateFrom)
        const to = new Date(dateTo)
        const totalDays = (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)

        if (start > to || end < from) return null

        const barStart = Math.max(0, (start.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
        const barEnd = Math.min(totalDays, (end.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
        const barDuration = barEnd - barStart

        if (barDuration <= 0) return null

        return {
            left: (barStart / totalDays) * 100,
            width: (barDuration / totalDays) * 100,
        }
    }

    const team = data?.team || []

    // Calculate cumulative load at any given date for a member (sum of all overlapping projects)
    const getLoadAtDate = (member: any, targetDate: Date): number => {
        const projects = member.opportunities || member.projects || []
        let totalLoad = 0

        projects.forEach((project: any) => {
            // The prácticas slot is shown but never adds to load.
            if (project.is_practicas) return
            // Prefer the backend-computed effective_start_date/effective_end_date
            // (which already honour the role's start_availability offset on top of
            // opportunity_users.fecha_inicio). Fall back to the raw opp-level dates
            // only for payloads that predate the A6 fix. Start-only = open-ended.
            if (windowOverlaps(projectWindow(project), targetDate, targetDate)) {
                totalLoad += project.capacity_pct || 0
            }
        })

        return totalLoad
    }

    // Get all date events (starts and ends) for a member's projects within a range
    const getLoadChangePoints = (member: any, rangeStart: Date, rangeEnd: Date): Date[] => {
        const projects = member.opportunities || member.projects || []
        const events: Date[] = [rangeStart, rangeEnd]

        projects.forEach((project: any) => {
            const startStr = project.effective_start_date || project.oferta_fecha_inicio || project.proyecto_fecha_inicio
            const endStr = project.effective_end_date || project.proyecto_fecha_fin || project.oferta_fecha_entrega

            if (startStr) {
                const start = new Date(startStr)
                start.setHours(0, 0, 0, 0)
                if (start > rangeStart && start < rangeEnd) {
                    events.push(start)
                }
            }
            if (endStr) {
                const end = new Date(endStr)
                end.setHours(0, 0, 0, 0)
                // Add day after end to capture when project ends
                const dayAfter = new Date(end)
                dayAfter.setDate(dayAfter.getDate() + 1)
                if (dayAfter > rangeStart && dayAfter < rangeEnd) {
                    events.push(dayAfter)
                }
            }
        })

        // Sort and deduplicate
        return [...new Set(events.map(d => d.getTime()))].sort((a, b) => a - b).map(t => new Date(t))
    }

    // Build segments for a bar with varying colors based on load at each point
    const buildBarSegments = (member: any, barStart: Date, barEnd: Date, projectCapacity: number) => {
        const changePoints = getLoadChangePoints(member, barStart, barEnd)
        const segments: { start: Date; end: Date; load: number }[] = []

        for (let i = 0; i < changePoints.length - 1; i++) {
            const segStart = changePoints[i]
            const segEnd = changePoints[i + 1]

            // Calculate load at segment midpoint
            const midTime = (segStart.getTime() + segEnd.getTime()) / 2
            const midDate = new Date(midTime)
            const loadAtMid = getLoadAtDate(member, midDate)

            segments.push({ start: segStart, end: segEnd, load: loadAtMid })
        }

        return segments
    }

    // Get color based on load percentage (total load from all coinciding projects)
    const getBarColor = (load: number): string => {
        if (load <= 50) return '#4CAF50'   // Green - low load
        if (load <= 80) return '#FF9800'   // Yellow/Orange - medium load
        return '#F44336'                    // Red - high/overloaded
    }

    // Get opacity based on load (more load = more intense)
    const getBarOpacity = (load: number): number => {
        if (load === 0) return 0.3
        if (load <= 30) return 0.5
        if (load <= 50) return 0.65
        if (load <= 80) return 0.8
        return 1.0
    }

    if (team.length === 0) {
        return (
            <ChartContainer>
                <EmptyMessage>No hay miembros del equipo</EmptyMessage>
            </ChartContainer>
        )
    }

    return (
        <ChartContainer>
            <ChartWrapper>
                <LabelsColumn>
                    <LabelHeader>Persona</LabelHeader>
                    {team.map((member: any) => {
                        // Use today_capacity for color (load calculated for today)
                        const todayLoad = member.today_capacity ?? 0
                        return (
                            <LabelRow
                                key={member.user_id}
                                borderColor={getLoadColor(todayLoad)}
                                title={`Carga hoy: ${todayLoad}% - Clic para ver desglose`}
                                onClick={() => onPersonClick(member)}
                            >
                                {member.display_name}
                            </LabelRow>
                        )
                    })}
                </LabelsColumn>

                <ChartArea>
                    <ChartHeader>
                        {periods.map((m, i) => (
                            <MonthCell key={i} colWidth={colWidth}>{m.label}</MonthCell>
                        ))}
                    </ChartHeader>

                    <RowsContainer>
                        {todayPosition !== null && (
                            <TodayLine left={todayPosition}>
                                <TodayLabel>Hoy</TodayLabel>
                            </TodayLine>
                        )}

                        {team.map((member: any) => {
                            const projects = member.opportunities || member.projects || []
                            const hasTasks = (member.assignments || []).length > 0

                            return (
                                <TimelineRow key={member.user_id} colWidth={colWidth}>
                                    {periods.map((_, i) => <div key={i} />)}

                                    {projects.map((project: any, idx: number) => {
                                        // Use backend-computed effective dates first so deferred-start
                                        // assignments (start_availability > 0) render at the correct offset.
                                        // Open-ended rows run to the edge of the visible range.
                                        const barWindow = projectWindow(project)
                                        if (!barWindow) return null

                                        // Build segments with varying colors based on load at each moment
                                        const segments = buildBarSegments(member, barWindow.start, barWindow.end, project.capacity_pct || 0)

                                        return segments.map((segment, segIdx) => {
                                            const segPos = calculateBarPosition(segment.start, segment.end)
                                            if (!segPos) return null

                                            const barColor = getBarColor(segment.load)
                                            const barOpacity = getBarOpacity(segment.load)

                                            return (
                                                <Bar
                                                    key={`${member.user_id}-${project.id}-${idx}-seg${segIdx}`}
                                                    left={segPos.left}
                                                    width={segPos.width}
                                                    color={barColor}
                                                    opacity={barOpacity}
                                                    top={hasTasks ? OPPORTUNITY_TOP_WITH_TASKS : OPPORTUNITY_TOP}
                                                    title={`${project.name} - Dedicación: ${project.capacity_pct || 0}% - Carga total: ${segment.load}%`}
                                                />
                                            )
                                        })
                                    })}

                                    {/* LinkedIn learning tasks: a single solid purple bar each,
                                        a separate lane that does NOT feed the load/overload math. */}
                                    {(member.assignments || []).map((assignment: any, idx: number) => {
                                        // Tasks without fecha_fin (the modal's default) are open-ended:
                                        // they used to be skipped, so most tasks never showed.
                                        const taskWindow = resolveWindow(assignment.effective_start_date, assignment.effective_end_date)
                                        if (!taskWindow) return null

                                        const pos = calculateBarPosition(taskWindow.start, taskWindow.end)
                                        if (!pos) return null

                                        const title = assignment.target_title || 'Tarea LinkedIn'
                                        const progress = typeof assignment.progress_pct === 'number'
                                            ? ` - Progreso: ${assignment.progress_pct}%`
                                            : ''

                                        return (
                                            <Bar
                                                key={`linkedin-${member.user_id}-${assignment.assignment_id ?? idx}`}
                                                left={pos.left}
                                                width={pos.width}
                                                color={linkedinColor}
                                                opacity={1}
                                                top={TASK_TOP}
                                                height={TASK_HEIGHT}
                                                title={`🎓 ${title}${progress}`}
                                            />
                                        )
                                    })}
                                </TimelineRow>
                            )
                        })}
                    </RowsContainer>
                </ChartArea>
            </ChartWrapper>
        </ChartContainer>
    )
}
